import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
	BankDataLookupOutcome,
	BankIdentifierValidationType,
} from '@dealstack/shared/lib/generated';
import { BANK_ACCOUNT_FORM_FIELDS, BankAccountFormValues } from './types.ts';
import { ruleUsesClassicIbanForm } from './BankAccountForm.utils.ts';
import {
	resolveFailureKind,
	resolveVerificationStatus,
	toVerificationResult,
	VERIFY_KEYS_BY_VALIDATION_TYPE,
} from './bankVerification.utils.ts';
import { verifyBankIdentifiers } from '@dealstack/frontend/shared/dataExchange/services/validation.service.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useBankAccountFormContext } from './BankAccountFormContext.tsx';
import type {
	BankAccountFormVerification,
	BankAccountVerificationResult,
} from './BankAccountFormContext.types.ts';
import type {
	DerivedField,
	UseBankAccountVerificationParams,
} from './useBankAccountVerification.types.ts';

export const VERIFY_QUERY_KEY = 'verifyBankIdentifiers';

// Owns every subscription to the typed identifier values, so a keystroke re-renders
// the mounted fieldset and nothing above it.
export const useBankAccountVerification = ({
	rule,
	verifyIdentifiers,
}: UseBankAccountVerificationParams): BankAccountFormVerification => {
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const { control, getValues, setValue, setError, clearErrors } =
		useFormContext<BankAccountFormValues>();
	const { bankAccount } = useBankAccountFormContext();

	const iban = useWatch({ control, name: BANK_ACCOUNT_FORM_FIELDS.iban });
	const identifierValues = useWatch({ control, name: BANK_ACCOUNT_FORM_FIELDS.identifiers });

	// Which form renders and what gets verified are separate decisions. An
	// IBAN-carrying rule renders the classic form, but the verification always comes
	// from the rule's own validationType — the backend derives it from the same row,
	// so the two can never disagree (a rule left at NONE simply offers no check).
	const isClassicIbanPath = !rule || ruleUsesClassicIbanForm(rule);
	const activeValidationType = rule ? rule.validationType : BankIdentifierValidationType.IBAN;
	const verifyKeys = VERIFY_KEYS_BY_VALIDATION_TYPE[activeValidationType];
	const verifyValues: Record<string, string> = isClassicIbanPath
		? { iban: iban ?? '' }
		: identifierValues ?? {};
	// Collision-free record of exactly what was verified: the query key resets, and a
	// previously applied result is dropped, as soon as any of these values changes.
	const verifySignature = JSON.stringify(
		verifyKeys.map((key) => (verifyValues[key] ?? '').trim())
	);
	const canVerify =
		verifyKeys.length > 0 && verifyKeys.every((key) => !!(verifyValues[key] ?? '').trim());

	const verifyRequest = useQuery({
		queryKey: [VERIFY_QUERY_KEY, rule?.id ?? 'classic-iban', verifySignature],
		queryFn: async (): Promise<BankAccountVerificationResult | null> => {
			if (!canVerify) {
				return null;
			}
			const dto = {
				formatRuleId: rule?.id,
				identifiers: verifyKeys.map((key) => ({
					key,
					value: (verifyValues[key] ?? '').trim(),
				})),
			};
			// A host-scoped verify fn logs the lookup against the host's (draft)
			// account; without one the generic validation endpoint answers.
			const response = verifyIdentifiers
				? await verifyIdentifiers(dto, {
						countryCode: getValues(BANK_ACCOUNT_FORM_FIELDS.country_code) ?? null,
					})
				: await verifyBankIdentifiers(dto);
			return toVerificationResult(response.payload);
		},
		enabled: false,
		retry: false,
		meta: { suppressErrorToast: true },
	});

	const verificationAnswer = verifyRequest.data ?? null;
	const verificationStatus = resolveVerificationStatus({
		isFetching: verifyRequest.isFetching,
		isError: verifyRequest.isError,
		outcome: verificationAnswer?.outcome ?? null,
	});

	// The INVALID error lands on the field the validation actually read (first key).
	const errorFieldName = isClassicIbanPath
		? BANK_ACCOUNT_FORM_FIELDS.iban
		: (`${BANK_ACCOUNT_FORM_FIELDS.identifiers}.${verifyKeys[0]}` as const);

	// Tracks the values a successful verification vouched for, so editing them can
	// drop the derived fields (verified country, looked-up bank name) it populated.
	// A result already in the cache when the scope mounts (Change country, then Back)
	// was applied by the previous scope, so it counts as applied but is not re-applied.
	const lastAppliedSignatureRef = useRef<string | null>(
		verificationAnswer?.outcome === BankDataLookupOutcome.SUCCESS ? verifySignature : null
	);

	const applyVerification = (result: BankAccountVerificationResult) => {
		if (result.outcome === BankDataLookupOutcome.INVALID) {
			// The provider's own message is audit evidence, not UI copy — the panel
			// explains the failure, the field just marks the value that caused it.
			setError(errorFieldName, {
				message: isClassicIbanPath
					? tCommonUi('profile.banking_details.iban.invalid')
					: tCapManage('bank_account.drawer.verification_failed.field_error', {
							defaultValue: 'This could not be verified',
						}),
			});
			return;
		}
		if (result.outcome === BankDataLookupOutcome.UNAVAILABLE) {
			// Surfaced through the warning box (status 'error'), never as field errors.
			return;
		}

		clearErrors();
		if (result.bankName) {
			setValue(BANK_ACCOUNT_FORM_FIELDS.bank_name, result.bankName);
		}
		if (result.bic) {
			setValue(BANK_ACCOUNT_FORM_FIELDS.bic, result.bic);
		}
		if (isClassicIbanPath && result.accountNumber) {
			setValue(BANK_ACCOUNT_FORM_FIELDS.account_number, result.accountNumber);
		}
		// Held off the visible country select so the lookup never changes what the user
		// sees — but it wins on save: toBankAccountPayload sends it AS countryCode, with
		// the selected country only as the fallback. There is no separate wire field.
		setValue(BANK_ACCOUNT_FORM_FIELDS.verified_country_code, result.countryCode ?? null);
		lastAppliedSignatureRef.current = verifySignature;
	};

	useEffect(() => {
		if (
			!verifyRequest.isSuccess ||
			!verifyRequest.data ||
			lastAppliedSignatureRef.current === verifySignature
		) {
			return;
		}
		applyVerification(verifyRequest.data);
	}, [verifyRequest.isSuccess, verifyRequest.data]);

	useEffect(() => {
		if (!verifyRequest.isError) {
			return;
		}
		setError(errorFieldName, {
			message: tCommonUi('bank_account_input.iban_lookup_error'),
		});
	}, [verifyRequest.isError]);

	// setValue notifies every subscriber even when nothing changed, so only write
	// fields that actually hold a value.
	const clearDerived = (names: readonly DerivedField[]) => {
		for (const name of names) {
			if (name === BANK_ACCOUNT_FORM_FIELDS.verified_country_code) {
				if (getValues(name) !== null) {
					setValue(name, null);
				}
			} else if (getValues(name) !== '') {
				setValue(name, '');
			}
		}
	};

	// The scope remounts with each tab, so only a real edit, never a mount, may clear
	// lookup-derived fields.
	const previousIbanRef = useRef(iban);
	useEffect(() => {
		if (previousIbanRef.current === iban) {
			return;
		}
		previousIbanRef.current = iban;
		// The lookup is cached under the value it verified, so retyping one already
		// looked up re-applies it in this same commit — clearing here would undo that
		// and leave the Verified card vouching for a payload it never filled in.
		if (lastAppliedSignatureRef.current === verifySignature) {
			return;
		}
		clearDerived([BANK_ACCOUNT_FORM_FIELDS.verified_country_code]);
		if (!bankAccount?.iban || iban !== bankAccount.iban) {
			// Everything a lookup derives from the IBAN, including the two fields the
			// IBAN tab never renders: left behind they belong to the previous bank, and
			// the user can neither see nor correct them before they are submitted.
			clearDerived([
				BANK_ACCOUNT_FORM_FIELDS.bank_name,
				BANK_ACCOUNT_FORM_FIELDS.bic,
				BANK_ACCOUNT_FORM_FIELDS.account_number,
			]);
		}
	}, [iban]);

	useEffect(() => {
		if (
			lastAppliedSignatureRef.current &&
			lastAppliedSignatureRef.current !== verifySignature
		) {
			lastAppliedSignatureRef.current = null;
			clearDerived([BANK_ACCOUNT_FORM_FIELDS.verified_country_code]);
			if (!isClassicIbanPath) {
				clearDerived([BANK_ACCOUNT_FORM_FIELDS.bank_name, BANK_ACCOUNT_FORM_FIELDS.bic]);
			}
		}
	}, [verifySignature]);

	const failureKind =
		verificationStatus === 'error'
			? resolveFailureKind(verificationAnswer, verifyRequest.isError)
			: null;
	const ibanMatchesStored = !!bankAccount?.iban && bankAccount.iban === iban;
	// A saved account's untouched IBAN shows its stored summary instead of re-verifying.
	const verifyDisabled = !canVerify || (isClassicIbanPath && ibanMatchesStored);

	return useMemo(
		() => ({
			onVerify: () => {
				if (canVerify) {
					void verifyRequest.refetch();
				}
			},
			status: verificationStatus,
			result:
				verificationAnswer?.outcome === BankDataLookupOutcome.SUCCESS
					? {
							bankName: verificationAnswer.bankName,
							accountNumber: verificationAnswer.accountNumber,
							bic: verificationAnswer.bic,
						}
					: null,
			failureKind,
			validationType: activeValidationType,
			verifyDisabled,
			ibanMatchesStored,
		}),
		[
			canVerify,
			verifyRequest.refetch,
			verificationStatus,
			verificationAnswer,
			failureKind,
			activeValidationType,
			verifyDisabled,
			ibanMatchesStored,
		]
	);
};
